/**
 * Copy the position of one background to the other products of the channel.
 *
 * Designers usually make one picture and then colour it for each product. The
 * pictures have the same size and the same composition; only the colours are
 * different. An admin positions the picture on the banners of one product, and
 * the other products need the same position. Before, the admin had to open
 * each product and do the same steps again.
 *
 * The panel finds the same picture in the gallery of each other product of the
 * channel (the same stage and set on Releases): by its composition, because
 * each product has it at a different place in the list (libs/bgFingerprint.ts).
 * Then it writes the position of each banner — position, zoom, turn, flip —
 * and, when chosen, the brightness and opacity and the colours of the banners
 * (base, text, logo), to the row of that background in each product. A logo
 * goes as the same style of the target product's own logo (logoVariantFor).
 *
 * The crop is in the pixels of the image (libs/BiggerPicture.js shows the image
 * at its natural size, scaled). A copy of the picture at another resolution
 * gets the zoom multiplied by the ratio of the widths, so the banner shows the
 * same part of the picture.
 *
 * This module has no React: ReplicateBgSection.tsx runs it.
 */
import {findChannelObj, productCodesForSelector} from "data/products.js";
import {releasesSetsForProduct} from "data/products/index";
import {homeScope} from "data/offering";
import {buildBgCatalogIndex, type CatalogBackgroundLike} from "libs/bgCatalogIndex";
import {numberedVariations} from "libs/bgNumbering";
import {fingerprintSimilarity, type BgFingerprint} from "libs/bgFingerprint";
import {LOGO_VARIANT_NONE} from "libs/logoVariantConstants";
import {BG_PREVIEW_TEMPLATE_FIELD} from "libs/bgPreviewTemplate";
import {stageProductCodes} from "libs/productOffer";
import {
  hiddenBackgroundsOf,
  hiddenSetsOf,
  isProductOffered,
  orderedBySet,
  setBackgroundOrderOf,
  type ChannelSettingsLike,
} from "libs/selectorVisibility";
import {galleryTiles} from "../BgSelector/galleryTiles";
import {shadowedCropTarget, type OverrideRow, type RowSpecificity} from "./cropRowTarget";
import {shadowingRowPatches} from "./shadowingRows";

/** The channels with a numbered gallery of their own per product. */
const EXCLUDED_CHANNELS = new Set(["livestream", "ads"]);

export function canReplicateOnChannel(channel: string): boolean {
  return !EXCLUDED_CHANNELS.has(channel);
}

export interface ReplicaProductsInput {
  /** The data channel and type of the page (data/offering.ts). */
  channel: string;
  type: string;
  set: string | null | undefined;
  /** The product on screen: it is the source, not a target. */
  product: string;
  /** The settings of the channel. On Releases they also hold the sets. */
  settings: ChannelSettingsLike;
}

/**
 * The products that can get the copy: the products the selectors offer on this
 * stage of the channel (or this type), which keep their data in the same
 * channel and type, and on Releases also have the set on screen.
 */
export function replicaProductCodes({
  channel,
  type,
  set,
  product,
  settings,
}: ReplicaProductsInput): string[] {
  const channelObj = findChannelObj(channel);
  if (!channelObj) throw new Error(`Unknown channel: ${channel}`);
  const codes: string[] = channelObj.typesAreStages
    ? stageProductCodes(settings, channel, type)
    : (productCodesForSelector(channel, type) ?? []);
  return [...new Set(codes)].filter((code) => {
    if (code === product || code === "ALL") return false;
    const home = homeScope(channel, type, code);
    if (home.channel !== channel || home.type !== type) return false;
    if (!isProductOffered(settings, code, type ? [type] : [])) return false;
    if (channel === "releases" && set) {
      const sets: Array<{value: string}> = releasesSetsForProduct(code);
      if (!sets.some((item) => item.value === set)) return false;
      if (hiddenSetsOf(settings, type, code).has(set)) return false;
    }
    return true;
  });
}

/** One numbered tile of the gallery of a product. */
export interface ReplicaTile {
  /** The identity of the background (what `bgOption` holds). */
  variation: string;
  /** The tile number on the product's page (libs/bgNumbering.ts). */
  number: number;
  imageUrl: string;
  thumbnailUrl: string;
  /**
   * Width / height as the catalog has it, or null. Only a hint for the order
   * of the comparisons: the catalog of an old background can have the size of
   * another image (see image-sizes in api/v3).
   */
  catalogAspect: number | null;
  /** "2356x1656" as the catalog has it, or null: a hint like `catalogAspect`. */
  catalogSize: string | null;
}

export interface GalleryInput {
  backgrounds: CatalogBackgroundLike[];
  product: string;
  type: string;
  set: string | null | undefined;
  settings: ChannelSettingsLike;
}

/**
 * The numbered tiles of a product's gallery, in the order its page shows them:
 * the order of the set, without the backgrounds hidden on the page. The same
 * functions number the tiles of the page (BgSelector/galleryTiles.ts).
 */
export function replicaGallery({backgrounds, product, type, set, settings}: GalleryInput): ReplicaTile[] {
  const displayOrder = orderedBySet(
    backgrounds.map((background) => background.variation),
    (variation) => variation,
    setBackgroundOrderOf(settings, type, product, set)
  );
  const tiles = galleryTiles({
    catalogIndex: buildBgCatalogIndex(backgrounds, product),
    displayOrder,
    allBgUrls: [],
    product,
    isAllProducts: false,
    isGoogleDisplayNetwork: false,
  });
  const numbered = numberedVariations(
    tiles.map((tile) => tile.variation),
    hiddenBackgroundsOf(settings, type, product, set)
  );
  const byVariation = new Map(tiles.map((tile) => [tile.variation, tile]));
  const catalogOf = new Map(backgrounds.map((background) => [background.variation, background]));
  return numbered.map((variation, index) => {
    const tile = byVariation.get(variation)!;
    const {width, height} = (catalogOf.get(variation) ?? {}) as {width?: number; height?: number};
    return {
      variation: String(variation),
      number: index + 1,
      imageUrl: tile.imageUrl,
      thumbnailUrl: tile.thumbnailUrl,
      catalogAspect: hasSize(width, height) ? width! / height! : null,
      catalogSize: hasSize(width, height) ? `${width}x${height}` : null,
    };
  });
}

function hasSize(width: unknown, height: unknown): boolean {
  return typeof width === "number" && typeof height === "number" && width > 0 && height > 0;
}

/** The same picture, coloured for the product. */
export const SAME_PICTURE = 0.9;
/** Alike, but not certainly the same picture: the admin checks it. */
export const SIMILAR_PICTURE = 0.75;
/** Aspects that differ by more than this are not the same picture. */
const ASPECT_TOLERANCE = 0.03;

/** Do two aspects (width / height) belong to the same picture? */
export function sameAspect(a: number | null, b: number | null): boolean {
  return a !== null && b !== null && Math.abs(Math.log(a / b)) <= ASPECT_TOLERANCE;
}

/**
 * The tiles to compare first, in groups: the tile with the identity of the
 * source (imports of one design give it to each product's copy), then the
 * tiles with the catalog aspect of the source. A comparison stops at the first
 * group that has the same picture; else every tile is compared.
 */
export function comparisonGroups(
  source: Pick<ReplicaTile, "variation" | "catalogAspect">,
  gallery: readonly ReplicaTile[]
): ReplicaTile[][] {
  const own = gallery.filter((tile) => tile.variation === source.variation);
  const aspect = gallery.filter(
    (tile) => tile.variation !== source.variation && sameAspect(tile.catalogAspect, source.catalogAspect)
  );
  return [own, [...own, ...aspect]].filter(
    (group, index, groups) => group.length > 0 && (index === 0 || group.length > groups[index - 1]!.length)
  );
}

export type MatchLevel = "same" | "similar" | "different";

export interface ReplicaMatch {
  tile: ReplicaTile;
  /** fingerprintSimilarity to the source; null when the thumbnail did not load. */
  similarity: number | null;
  level: MatchLevel;
  /** The aspect of the image is not the aspect of the source. */
  otherAspect: boolean;
}

export interface CandidateFingerprint {
  tile: ReplicaTile;
  fingerprint: BgFingerprint | null;
}

/**
 * The tiles of a product, best match first. The composition decides. When two
 * tiles are nearly as alike (the same picture twice, in two sizes), the tile
 * with the identity of the source goes first: imports of one design give one
 * identity to each product's copy of a picture (FigBaker, Social). Then the
 * tile with the size of the source: a recoloured export keeps the size.
 */
export function rankMatches(
  source: {variation: string; catalogSize?: string | null; fingerprint: BgFingerprint},
  candidates: readonly CandidateFingerprint[]
): ReplicaMatch[] {
  const scored = candidates.map(({tile, fingerprint}) => {
    if (!fingerprint) {
      return {match: {tile, similarity: null, level: "different", otherAspect: false} as ReplicaMatch, score: -2};
    }
    const similarity = fingerprintSimilarity(source.fingerprint, fingerprint);
    const otherAspect = !sameAspect(fingerprint.aspect, source.fingerprint.aspect);
    let level: MatchLevel =
      similarity >= SAME_PICTURE ? "same" : similarity >= SIMILAR_PICTURE ? "similar" : "different";
    if (otherAspect && level === "same") level = "similar";
    const sameSize = Boolean(source.catalogSize) && tile.catalogSize === source.catalogSize;
    const score =
      similarity +
      (tile.variation === source.variation ? 0.05 : 0) +
      (sameSize ? 0.03 : 0) -
      (otherAspect ? 0.2 : 0);
    return {match: {tile, similarity, level, otherAspect}, score};
  });
  return scored
    .sort((a, b) => b.score - a.score || a.match.tile.number - b.match.tile.number)
    .map(({match}) => match);
}

export interface BgTransform {
  x: number;
  y: number;
  scale: number;
  rotate: number;
  flip: {x: boolean; y: boolean};
}

export interface ImageSize {
  width: number;
  height: number;
}

/**
 * The crop of the source image, for the same picture at another size: the
 * zoom times the ratio of the widths. The offset is in banner pixels and does
 * not change.
 */
export function fitTransform(
  transform: BgTransform,
  sourceSize: ImageSize,
  targetSize: ImageSize
): BgTransform {
  if (!(sourceSize.width > 0) || !(targetSize.width > 0)) {
    throw new Error("Both image sizes are necessary to copy a crop");
  }
  return {
    x: transform.x,
    y: transform.y,
    scale: transform.scale * (sourceSize.width / targetSize.width),
    rotate: transform.rotate,
    flip: {x: Boolean(transform.flip.x), y: Boolean(transform.flip.y)},
  };
}

/** What one banner of the page gives the copy. */
export interface ReplicaSource {
  /** The banner's crop on the source image. */
  transform: BgTransform;
  /** The background box of the banner, in template pixels (for the bake). */
  width: number;
  height: number;
  /** The banner's own opacity and brightness, when it has them. */
  opacity?: number;
  brightness?: number;
}

/** The look of the whole background, when the copy takes it. */
export interface ReplicaLook {
  bgBrightness?: number;
  bgOpacity?: number;
  bgBlurValue?: number;
}

export interface ReplicaTemplateInput {
  /** The banners the copy has a source for. */
  sourceIds: readonly string[];
  /** The banners the target product's page draws. */
  targetIds: readonly string[];
  /** The target route's resolved default scene. */
  targetScene: {customBgs?: Record<string, unknown> | null} | null | undefined;
}

export interface SkippedTemplate {
  id: string;
  reason: "not-on-page" | "own-image";
}

/**
 * The banners of a target that get the copy. A banner that the target's page
 * does not draw gets nothing. Nor does a banner that has an image of its own
 * for the background (a ready cut, a banner upload): the crop of the gallery
 * picture is not a crop of that image.
 */
export function replicaTemplateIds({
  sourceIds,
  targetIds,
  targetScene,
}: ReplicaTemplateInput): {ids: string[]; skipped: SkippedTemplate[]} {
  const onPage = new Set(targetIds);
  const ids: string[] = [];
  const skipped: SkippedTemplate[] = [];
  for (const id of sourceIds) {
    if (!onPage.has(id)) skipped.push({id, reason: "not-on-page"});
    else if (targetScene?.customBgs?.[id]) skipped.push({id, reason: "own-image"});
    else ids.push(id);
  }
  return {ids, skipped};
}

/** The record fields the copy writes, per banner. */
const CROP_RECORDS = [
  ["bgX", "x"],
  ["bgY", "y"],
  ["bgScale", "scale"],
  ["bgRotate", "rotate"],
  ["bgFlip", "flip"],
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export interface ReplicaPatchInput {
  /** The row's scene data before the write. */
  previous: Record<string, unknown> | null | undefined;
  /** The banners that get the copy (replicaTemplateIds). */
  templateIds: readonly string[];
  sources: Readonly<Record<string, ReplicaSource>>;
  sourceSize: ImageSize;
  targetSize: ImageSize;
  /** The look of the whole background; null when the copy does not take the look. */
  look: ReplicaLook | null;
  /** The banner the selector draws the background's tile with. */
  previewTemplate: string | null;
  /** The colours of the banners; null when the copy does not take them. */
  colours?: ReplicaColours | null;
  /** The target route's resolved scene: it tells where an empty value must be written. */
  targetScene?: Record<string, unknown> | null;
  /** The target's logo for a source logo; undefined: the target keeps its own. */
  logoFor?: (logo: string | null) => string | null | undefined;
}

export interface ReplicaPatch {
  /** Whole field values: a PATCH replaces each field it names. */
  sceneData: Record<string, unknown>;
  /** The crop of each banner, on the target image (for the bake). */
  transforms: Record<string, BgTransform>;
}

/**
 * The fields to write to the target row: each per-banner record as the row
 * has it, with the copied banners put in.
 */
export function replicaPatch({
  previous,
  templateIds,
  sources,
  sourceSize,
  targetSize,
  look,
  previewTemplate,
  colours,
  targetScene,
  logoFor,
}: ReplicaPatchInput): ReplicaPatch {
  const record = (field: string): Record<string, unknown> => {
    const own = previous?.[field];
    return isRecord(own) ? {...own} : {};
  };
  const sceneData: Record<string, unknown> = {};
  const crops = Object.fromEntries(CROP_RECORDS.map(([field]) => [field, record(field)]));
  const opacities = record("bgOpacities");
  const brightnesses = record("bgBrightnesses");
  const transforms: Record<string, BgTransform> = {};
  for (const id of templateIds) {
    const source = sources[id];
    if (!source) throw new Error(`No source for banner ${id}`);
    const transform = fitTransform(source.transform, sourceSize, targetSize);
    transforms[id] = transform;
    for (const [field, key] of CROP_RECORDS) crops[field]![id] = transform[key];
    if (look) {
      if (source.opacity !== undefined) opacities[id] = source.opacity;
      if (source.brightness !== undefined) brightnesses[id] = source.brightness;
    }
  }
  Object.assign(sceneData, crops);
  if (look) {
    if (templateIds.some((id) => sources[id]?.opacity !== undefined)) sceneData.bgOpacities = opacities;
    if (templateIds.some((id) => sources[id]?.brightness !== undefined)) {
      sceneData.bgBrightnesses = brightnesses;
    }
    for (const [field, value] of Object.entries(look)) {
      if (value !== undefined) sceneData[field] = value;
    }
  }
  if (previewTemplate && templateIds.includes(previewTemplate)) {
    sceneData[BG_PREVIEW_TEMPLATE_FIELD] = previewTemplate;
  }
  if (colours) {
    Object.assign(
      sceneData,
      colourPatch({previous, templateIds, colours, targetScene, logoFor: logoFor ?? ((logo) => logo)})
    );
  }
  return {sceneData, transforms};
}

/**
 * The colours of the banners of a background (lookRowTarget.ts
 * BG_COLOUR_FIELDS), as the page shows them: the base of the background,
 * and per banner its base, its text colour and its logo.
 */
export interface ReplicaColours {
  base: string | null | undefined;
  bases: Record<string, unknown> | null | undefined;
  textColorLocked: Record<string, unknown> | null | undefined;
  logoVariants: Record<string, unknown> | null | undefined;
}

const PER_BANNER_COLOURS = ["bases", "textColorLocked", "logoVariants"] as const;

const isEmpty = (value: unknown) => value === undefined || value === null || value === "";

/**
 * The colour fields for the target row. The base goes as it is. Per banner,
 * a value of the source goes into the record; a banner without one follows
 * the page in the source, so in the target it must follow too: the record
 * gets null when the target's cascade gives the banner a value (a lower row
 * would show it), and loses the banner when it does not. A record that the
 * copy does not change is not written.
 */
function colourPatch({
  previous,
  templateIds,
  colours,
  targetScene,
  logoFor,
}: {
  previous: Record<string, unknown> | null | undefined;
  templateIds: readonly string[];
  colours: ReplicaColours;
  targetScene: Record<string, unknown> | null | undefined;
  logoFor: (logo: string | null) => string | null | undefined;
}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!isEmpty(colours.base)) out.base = colours.base;
  for (const field of PER_BANNER_COLOURS) {
    const own = previous?.[field];
    const record: Record<string, unknown> = isRecord(own) ? {...own} : {};
    const before = JSON.stringify(record);
    const resolved = isRecord(targetScene?.[field]) ? (targetScene![field] as Record<string, unknown>) : {};
    for (const id of templateIds) {
      let value = colours[field]?.[id];
      if (field === "logoVariants") {
        const logo = logoFor(isEmpty(value) ? null : String(value));
        if (logo === undefined) continue;
        value = logo;
      }
      if (!isEmpty(value)) record[id] = value;
      else if (!isEmpty(resolved[id])) record[id] = null;
      else delete record[id];
    }
    if (JSON.stringify(record) !== before) out[field] = record;
  }
  return out;
}

/** A logo URL as brand and style: "JETBRAINS_IDEs" and "white_logo_white_hole". */
export function logoStyleOf(url: string): {brand: string; style: string} | null {
  const name = decodeURIComponent(url.split(/[?#]/)[0]!.split("/").pop() ?? "").replace(/\.[a-z0-9]+$/i, "");
  const words = name.split("_");
  const start = words.findIndex((word) => /^(logo|white|black)$/i.test(word.trim()));
  if (start <= 0) return null;
  return {
    brand: words.slice(0, start).join("_").trim().toLowerCase(),
    style: words.slice(start).join("_").toLowerCase(),
  };
}

/**
 * The logo of the target product for a logo of the source product. Logos are
 * per brand: a JetBrains Data logo is not a logo of WebStorm. A source logo of
 * the source product's own brand (`sourcePreferred`) goes as the logo of the
 * same style (black, white, inverted…) of the target's own brand. A logo of
 * another brand, which an admin chose on purpose (the "JetBrains IDEs" lockup
 * on IntelliJ IDEA), goes as it is when the target's logo cycle has it. Else
 * the same style of the target's brand, else of any brand in the cycle. Null
 * (the logo follows the background) and "no logo" go as they are. Undefined
 * when the cycle has no logo of that style: the target keeps its own.
 */
export function logoVariantFor(
  logo: string | null,
  {cycle, preferred}: {cycle: ReadonlyArray<string | null>; preferred: string | null},
  sourcePreferred: string | null = null
): string | null | undefined {
  if (logo === null || logo === LOGO_VARIANT_NONE) return logo;
  const source = logoStyleOf(logo);
  if (!source) return cycle.includes(logo) ? logo : undefined;
  const sameStyle = cycle.filter(
    (candidate): candidate is string =>
      typeof candidate === "string" && logoStyleOf(candidate)?.style === source.style
  );
  const targetBrand = preferred ? logoStyleOf(preferred)?.brand : undefined;
  const ownBrand = sameStyle.find((candidate) => logoStyleOf(candidate)?.brand === targetBrand);
  const isSourceBrand = Boolean(sourcePreferred) && logoStyleOf(sourcePreferred!)?.brand === source.brand;
  if (!isSourceBrand && cycle.includes(logo)) return logo;
  return ownBrand ?? (cycle.includes(logo) ? logo : sameStyle[0]);
}

export interface ReplicaRowInput {
  overrides: OverrideRow[] | null | undefined;
  channel: string;
  /** The target product. */
  product: string;
  type: string;
  set: string | null | undefined;
  /** The layout on screen: the target route has the same one. */
  layout: string;
  /** The matched background of the target. */
  background: string;
  /**
   * The layout of the row the source crop comes from, when that row is one of
   * a layout and a background. Null for a row of the background alone.
   */
  sourceLayout: string | null;
}

/**
 * The row of the target that gets the copy: the row of the matched background,
 * for the layout too when the source crop is for one layout. When a more
 * specific row of the target route carries a crop, that row would hide the
 * copy. Then the copy goes to the row of the layout and the background, which
 * outranks it and still belongs to this background alone.
 *
 * Throws when even that row is hidden: only a brand treatment row (JetBrains
 * Air) outranks it.
 */
export function replicaRowAddress({
  overrides,
  channel,
  product,
  type,
  set,
  layout,
  background,
  sourceLayout,
}: ReplicaRowInput): RowSpecificity {
  const chosen: RowSpecificity = {layout: sourceLayout, background, variant: null};
  const winner = shadowedCropTarget({
    overrides,
    channel,
    product,
    type,
    set,
    layout,
    background,
    variant: null,
    chosenProduct: product,
    chosenLayout: chosen.layout,
    chosenBackground: background,
    chosenVariant: null,
  });
  if (!winner) return chosen;
  if (winner.variant) {
    throw new Error(`A brand treatment row of ${product} hides the crop of background ${background}`);
  }
  if (winner.background === background) return winner;
  return {layout, background, variant: null};
}

export interface ReplicaWrite {
  address: RowSpecificity & {product: string};
  sceneData: Record<string, unknown>;
  /** The row before the write, for Undo; null when there was no row. */
  previous: Record<string, unknown> | null;
}

const sameAddress = (row: OverrideRow, product: string, address: RowSpecificity) =>
  row.product === product &&
  (row.layout || null) === (address.layout || null) &&
  (row.background || null) === (address.background || null) &&
  (row.variant || null) === (address.variant || null);

/** The target row, as the channel's list of rows has it. */
export function findRow(
  overrides: OverrideRow[] | null | undefined,
  {channel, type, set, product, address}: {
    channel: string;
    type: string;
    set: string | null | undefined;
    product: string;
    address: RowSpecificity;
  }
): OverrideRow | null {
  return (
    (overrides ?? []).find(
      (row) =>
        row.channel === channel &&
        row.type === type &&
        (row.set || "") === (set || "") &&
        sameAddress(row, product, address)
    ) ?? null
  );
}

/**
 * The writes of one target: its row, and the more specific rows of its route
 * that carry a copied field too (shadowingRows.ts) — else they would hide it.
 */
export function replicaWrites({
  overrides,
  channel,
  product,
  type,
  set,
  layout,
  address,
  sceneData,
}: {
  overrides: OverrideRow[] | null | undefined;
  channel: string;
  product: string;
  type: string;
  set: string | null | undefined;
  layout: string;
  address: RowSpecificity;
  sceneData: Record<string, unknown>;
}): ReplicaWrite[] {
  const row = findRow(overrides, {channel, type, set, product, address});
  const writes: ReplicaWrite[] = [
    {address: {product, ...address}, sceneData, previous: row?.scene_data ?? null},
  ];
  const shadows = shadowingRowPatches({
    overrides,
    channel,
    product,
    type,
    set,
    layout,
    background: address.background,
    variant: null,
    chosen: {product, ...address},
    patch: sceneData,
  });
  for (const shadow of shadows) {
    writes.push({
      address: {
        product: shadow.product,
        layout: shadow.layout,
        background: shadow.background,
        variant: shadow.variant,
      },
      sceneData: shadow.sceneData,
      previous: shadow.previous,
    });
  }
  return writes;
}

/** Lower case letters and digits only: "IntelliJ IDEA" → "intellijidea". */
const compact = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, "");

/**
 * The product a file of a batch is for, from its name: the name of a product
 * in it ("bg01-PyCharm.png"), or its code as a separate word ("01_bg_PC.png").
 * The longest product name in the file name wins, so "dotMemory" is not
 * "dotM…"; a name wins over a code. Null when the name names no product, or
 * names two products as codes only.
 */
export function productOfFileName(
  fileName: string,
  products: ReadonlyArray<{value: string; label: string}>
): string | null {
  const base = fileName.replace(/\.[a-z0-9]+$/i, "");
  const whole = compact(base);
  const byName = products
    .filter((p) => compact(p.label).length > 2 && whole.includes(compact(p.label)))
    .sort((a, b) => compact(b.label).length - compact(a.label).length);
  if (byName.length > 0) return byName[0]!.value;
  const words = new Set(base.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean));
  const byCode = products.filter((p) => words.has(p.value.toLowerCase()));
  return byCode.length === 1 ? byCode[0]!.value : null;
}
